import { randomUUID } from '@tasted/utils/randomId';
// DEV: sessionStorage keeps each browser window isolated (multi-window testing).
// Prod: localStorage persists through page refreshes on a player's own device.
const store = import.meta.env.DEV ? sessionStorage : localStorage;

// TAS-423 review fix: a boolean "did the write land" wasn't enough. A
// private window or an in-app browser that refuses writes leaves the
// PERSISTENT store either empty or — worse — holding a PREVIOUS player's id
// from an earlier game on this device, since a failed `setItem` never
// touches what was already there. Reading straight from the store after that
// either bounces the new player back to /join (AvatarSelect's TAS-416 check)
// or, worse, silently updates the OLD player's row with the OLD player's
// still-valid token. Once `set()` runs, this module-level mirror is the
// authoritative answer for the rest of this browser session (it dies on an
// actual page reload, same as any other in-memory state) — `get()` prefers
// it over the store whenever it has been written to, and only falls back to
// the store for the ordinary case: a fresh page load in a browser whose
// storage worked fine last time.
//
// Round-2 review fix: that mirror had no way to represent "explicitly
// cleared" separately from "never touched this session, go check the
// store". clear() set the mirror to null/deleted the map entry — which
// `get()` treated exactly like "not in the mirror", so it fell straight
// through to the store. A store whose removeItem also fails (same broken
// browser) then RESURRECTS whatever was there before: persist player A →
// set B while every write fails (mirror=B, store still=A, since set() never
// got to overwrite it) → clear() (mirror cleared, store removeItem also
// fails, store still=A) → get() falls back to the store and hands back A,
// a player who isn't even in this game anymore. CLEARED is a sentinel the
// mirror can hold that `get()` honours over the store — cleared is
// authoritative until the next `set()`, exactly like a real value is.
const CLEARED = Symbol('playerStorage:cleared'); // explicitly cleared — authoritative empty, ignore the store

const memoryPlayerIds = new Map();    // storage key -> player id | CLEARED
const memoryClaimTokens = new Map();  // playerId -> token | CLEARED
const memoryJoinTokens = new Map();   // key -> token | CLEARED

/** Read a keyed mirror. Returns `undefined` when the key was never touched
 *  this session (caller should fall back to the store), `null` when it was
 *  explicitly cleared (authoritative — do not fall back), or the value. */
function readMirror(map, key) {
  if (!map.has(key)) return undefined;
  const v = map.get(key);
  return v === CLEARED ? null : v;
}

// A14 (edge-case audit 2026-09-28): the player id was one device-wide key, so
// a phone was one player across every game it had joined — friction on a
// host's or a shared phone. It is now also kept per game, and every reader
// that knows its game (every in-game route carries ?code=) reads that.
//
// `currentPlayerId` stays as "the last player this phone stored", for the few
// readers with no game in hand. It is also the one-time migration source: a
// phone that joined on the old bundle has only that key, so the first game to
// ask adopts it and nobody mid-game loses their seat. After that the phone is
// marked as keyed-by-game and the old key is never adopted again — a new game
// no longer inherits the last game's player.
const LEGACY_PLAYER_KEY = 'currentPlayerId';
const KEYED_MARKER = 'currentPlayerIdByGame';
const playerKey = (code) => `currentPlayerId:${code}`;

function normalizeCode(code) {
  const c = String(code ?? '').trim().toUpperCase();
  return c || null;
}

/**
 * The game this page is for, when a caller doesn't say: `?code=` (every
 * in-game route carries it), else `/join/:code`. null when neither is there.
 */
export function currentSessionCode() {
  try {
    if (typeof window === 'undefined' || !window.location) return null;
    const fromQuery = new URLSearchParams(window.location.search || '').get('code');
    if (fromQuery) return normalizeCode(fromQuery);
    const m = String(window.location.pathname || '').match(/^\/join\/([^/?#]+)/);
    return m ? normalizeCode(decodeURIComponent(m[1])) : null;
  } catch {
    return null;
  }
}

function readPlayerKey(key) {
  const mirrored = readMirror(memoryPlayerIds, key);
  if (mirrored !== undefined) return mirrored;
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
}

/** Write-through: mirror first, then the store. False when the store refused. */
function writePlayerKey(key, value) {
  memoryPlayerIds.set(key, value);
  try {
    store.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function resolveCode(code) {
  return code === undefined ? currentSessionCode() : normalizeCode(code);
}

export const playerStorage = {
  /** This phone's player id for game `code` (default: the game in the URL). */
  get: (code) => {
    const game = resolveCode(code);
    if (!game) return readPlayerKey(LEGACY_PLAYER_KEY);
    const key = playerKey(game);
    const own = readPlayerKey(key);
    if (own || readMirror(memoryPlayerIds, key) === null) return own;
    // One-time migration (see above): only a phone that has never stored an
    // id by game adopts the old device-wide one.
    if (readPlayerKey(KEYED_MARKER)) return null;
    const legacy = readPlayerKey(LEGACY_PLAYER_KEY);
    if (!legacy) return null;
    writePlayerKey(key, legacy);
    writePlayerKey(KEYED_MARKER, '1');
    return legacy;
  },
  // TAS-423 (game 7105's shape): join_game_session/rejoin_session_secure has
  // already SUCCEEDED server-side by the time this runs. Write-through: the
  // in-memory mirror is set FIRST and unconditionally, so the identity is
  // usable via get() immediately regardless of whether the persistent write
  // below lands — a caller may treat the join as complete as soon as this
  // returns, on either branch. The boolean return is for the caller to warn,
  // not to gate on.
  set: (id, code) => {
    const game = resolveCode(code);
    let ok = writePlayerKey(LEGACY_PLAYER_KEY, id);
    if (game) {
      ok = writePlayerKey(playerKey(game), id) && ok;
      writePlayerKey(KEYED_MARKER, '1');
    }
    return ok;
  },
  clear: (code) => {
    const game = resolveCode(code);
    const keys = game ? [playerKey(game), LEGACY_PLAYER_KEY] : [LEGACY_PLAYER_KEY];
    for (const key of keys) {
      memoryPlayerIds.set(key, CLEARED);
      try {
        store.removeItem(key);
      } catch {
        /* mirror is authoritative now — the stale store value is unreachable via get() */
      }
    }
  },
};

// TAS-190: the game-master's signed advance token (from claim_gm_secret),
// namespaced per session so it survives a reconnect/refresh on the same device
// and can't leak across sessions. Uses the same DEV/prod store as the player id.
const gmSecretKey = (sessionCode) => `gmSecret:${sessionCode}`;

export const gmSecretStorage = {
  get: (sessionCode) => (sessionCode ? store.getItem(gmSecretKey(sessionCode)) : null),
  set: (sessionCode, secret) => {
    if (sessionCode && secret) store.setItem(gmSecretKey(sessionCode), secret);
  },
  clear: (sessionCode) => {
    if (sessionCode) store.removeItem(gmSecretKey(sessionCode));
  },
};

// TAS-163: the one-time guest claim token (minted atomically with the join by
// join_session_and_mint), namespaced per player id. Presented to
// claim_game_score when the player links their game to an account — proof this
// device is the one that joined, so a stranger who merely learns the player id
// can't claim the game. Lives in the same store as currentPlayerId: losing one
// loses both, so the token adds no new way to get locked out.
const claimTokenKey = (playerId) => `claimToken:${playerId}`;

// Persist before sending the join so an unacknowledged insert is recoverable.
const joinTokenKey = (code, name) => `joinToken:${code}:${name}`;

function readJoinToken(key) {
  const mirrored = readMirror(memoryJoinTokens, key);
  if (mirrored !== undefined) return mirrored;
  return store.getItem(key);
}

export const pendingJoinTokenStorage = {
  get: (code, name) => readJoinToken(joinTokenKey(code, name)),
  getOrCreate: (code, name) => {
    const key = joinTokenKey(code, name);
    const existing = readJoinToken(key);
    if (existing) return existing;
    const token = randomUUID();
    // TAS-423 review fix (part 2): this used to run unguarded, and it fires
    // BEFORE join_game_session — a store that refuses every write threw
    // here first, so the player never even reached the RPC and still saw
    // "check your connection". Mirror it in memory first (so a same-tab
    // retry reuses this exact token — the dedupe join_game_session relies on
    // to avoid inserting a second row) and only warn if the persistent
    // write fails; it must not block the join that follows. console.warn,
    // not the Sentry logger: this is a low-level module imported almost
    // everywhere, and pulling in ./sentry here made it a transitive import
    // of every single test file, breaking any that mock @sentry/react
    // without a `logger` key (vitest's strict mock proxy throws on that).
    memoryJoinTokens.set(key, token);
    try {
      store.setItem(key, token);
    } catch {
      console.warn('[playerStorage] Could not persist join token to storage', { sessionCode: code, username: name });
    }
    return token;
  },
  // Round-2 review note: a full reset (DevResetButton's localStorage.clear()
  // + sessionStorage.clear()) wipes every persisted join token at once, but
  // has no single (code, name) key to hand this module to clear the mirror
  // through the normal path. Clear the whole thing instead.
  clearAll: () => {
    for (const key of memoryJoinTokens.keys()) {
      try {
        store.removeItem(key);
      } catch {
        /* best effort — mirror is cleared below regardless */
      }
    }
    memoryJoinTokens.clear();
  },
};

// A9: fired on window whenever a claim token is stored, so the request queue
// can send durable writes it parked on the old (refused) token. An event, not
// an import: the queue already imports this module.
export const CLAIM_TOKEN_CHANGED_EVENT = 'tasted:claim-token-changed';

function announceClaimToken(playerId) {
  try {
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new CustomEvent(CLAIM_TOKEN_CHANGED_EVENT, { detail: { playerId } }));
    }
  } catch {
    /* a listener failure must not fail the join */
  }
}

export const claimTokenStorage = {
  get: (playerId) => {
    if (!playerId) return null;
    const mirrored = readMirror(memoryClaimTokens, playerId);
    if (mirrored !== undefined) return mirrored;
    return store.getItem(claimTokenKey(playerId));
  },
  // TAS-423: same reasoning as playerStorage.set above — this always runs
  // right after a join/rejoin the server already accepted, so a throwing
  // store must not read back to the caller as a failed join. Write-through
  // to the in-memory mirror first.
  set: (playerId, token) => {
    if (!playerId || !token) return false;
    memoryClaimTokens.set(playerId, token);
    let persisted = false;
    try {
      store.setItem(claimTokenKey(playerId), token);
      persisted = true;
    } catch {
      persisted = false;
    }
    announceClaimToken(playerId);
    return persisted;
  },
  // Round-2 review fix: this used to `delete` the mirror entry, which reads
  // right back as "never touched" — readMirror falls through to the store,
  // resurrecting a token that removeItem failed to actually remove. Set the
  // CLEARED sentinel instead, same as playerStorage.clear() above.
  clear: (playerId) => {
    if (!playerId) return;
    memoryClaimTokens.set(playerId, CLEARED);
    try {
      store.removeItem(claimTokenKey(playerId));
    } catch {
      /* mirror is authoritative now */
    }
  },
};

// Round-2 review note: memoryClaimTokens/memoryJoinTokens (and, before this
// round, memoryPlayerId) never reset between tests in the same file — a
// later test can find a previous test's mirrored token for a coincidentally
// reused key (`joinToken:1234:Amy` shows up constantly, since most fixtures
// use the same code and name). Test setup calls this in beforeEach; nothing
// in application code should ever call it.
export function __resetForTests() {
  memoryPlayerIds.clear();
  memoryClaimTokens.clear();
  memoryJoinTokens.clear();
}
